import React, { useCallback, useMemo } from 'react';
import { IconButton, ListSubheader, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { TimeSignal } from 'graphql/types';
import { useMeContext } from 'modules/me';
import { NotesListGroupHeaderCheckbox } from './NotesListGroupHeaderCheckbox';
import { NotesListGroupDuration } from './NotesListGroupDuration';

type NotesListDayGroupHeaderType = {
  day: string;
  onExpandCollapse: (day: string) => void;
  expanded: boolean;
  notes: TimeSignal[];
  totalCount: number;
  totalDuration?: number;
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.table?.header,
    margin: theme.spacing(0.25, 0),
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    cursor: 'pointer',
    top: theme.spacing(-0.1)
  },
  icon: {
    padding: theme.spacing(0.25),
    marginRight: theme.spacing(1.125)
  },
  iconColor: {
    color: theme.palette.text.primary
  },
  title: {
    color: theme.palette.text.primary,
    paddingLeft: theme.spacing(0.25)
  },
  daySelectionWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  expand: {
    display: 'flex',
    marginRight: theme.spacing(-0.25)
  },
  sticky: {
    zIndex: 10
  }
}));

export const NotesListDayGroupHeader: React.FC<NotesListDayGroupHeaderType> = ({
  day,
  onExpandCollapse,
  expanded,
  notes,
  totalCount,
  totalDuration
}: NotesListDayGroupHeaderType) => {
  const classes = useStyles();
  const me = useMeContext();
  const luxonFormat =
    me?.locale?.dateTimeSettings?.shortDateFormat?.luxonPattern ||
    'MMM d, yyyy';

  const onClick = useCallback(() => {
    onExpandCollapse(day);
  }, [day, onExpandCollapse]);

  const { collapseIcon, expandIcon } = useMemo(
    () => ({
      collapseIcon: (
        <Tooltip title={<FormattedMessage id="collapse" />}>
          <IconButton
            className={classes.icon}
            data-testid="expand-less-icon"
            size="large"
          >
            <ExpandLessIcon className={classes.iconColor} />
          </IconButton>
        </Tooltip>
      ),
      expandIcon: (
        <Tooltip title={<FormattedMessage id="expand" />}>
          <IconButton
            className={classes.icon}
            data-testid="expand-more-icon"
            size="large"
          >
            <ExpandMoreIcon className={classes.iconColor} />
          </IconButton>
        </Tooltip>
      )
    }),
    [classes.icon, classes.iconColor]
  );
  const listSubheaderClasses = useMemo(
    () => ({
      sticky: classes.sticky
    }),
    [classes.sticky]
  );

  return (
    <ListSubheader
      data-testid={`notes-list-day-group-header-${day}`}
      className={classes.container}
      onClick={onClick}
      classes={listSubheaderClasses}
    >
      <div className={classes.daySelectionWrapper}>
        <NotesListGroupHeaderCheckbox
          groupKey={day}
          notes={notes}
          totalCount={totalCount}
        />
        <Typography
          data-testid="day-header"
          variant="caption"
          className={classes.title}
        >
          {DateTime.fromISO(day).toFormat(luxonFormat)}
        </Typography>
      </div>
      <div className={classes.expand}>
        <NotesListGroupDuration
          notes={notes}
          groupKey={day}
          totalDuration={totalDuration}
        />
        {expanded ? collapseIcon : expandIcon}
      </div>
    </ListSubheader>
  );
};

export default React.memo(NotesListDayGroupHeader);
